import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Loader2, Radar, Copy, Download, Clock } from "lucide-react";

const COHORTS = [
  { key: "stalled_lesson_1", label: "Started, didn't finish Lesson 1", tone: "accent" },
  { key: "lesson_1_not_golden_five", label: "Finished Lesson 1, not Golden Five", tone: "primary" },
  { key: "never_logged_in", label: "Signed up, never logged in", tone: "muted" },
];

export default function ReactivationCenter() {
  const { user, ready } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("stalled_lesson_1");

  useEffect(() => {
    api.get("/admin/reactivation").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (ready && (!user || user.role !== "admin")) return <Navigate to="/dashboard" replace />;
  if (loading || !data)
    return (
      <div className="min-h-screen"><Navbar />
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </div>
    );

  const rows = data.cohorts[tab] || [];
  const emails = rows.map((r) => r.email).join(", ");

  const copyEmails = () => {
    navigator.clipboard.writeText(emails);
    toast.success(`Copied ${rows.length} email${rows.length === 1 ? "" : "s"}`);
  };
  const downloadCsv = () => {
    const head = "name,email,status,last_active,days_since_active,signup_date\n";
    const body = rows.map((r) =>
      [r.name, r.email, r.status, r.last_active || "", r.days_since_active ?? "", r.signup_date || ""]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([head + body], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `reactivation-${tab}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <Radar className="h-6 w-6 text-primary" />
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Growth · Re-engagement</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mb-6">Reactivation Center</h1>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {COHORTS.map((c) => (
            <button key={c.key} onClick={() => setTab(c.key)} data-testid={`cohort-tab-${c.key}`}
              className={`p-4 rounded-sm border text-left transition-colors ${
                tab === c.key ? "border-primary/60 bg-primary/10" : "border-border/60 bg-card hover:border-primary/30"}`}>
              <div className="font-mono text-2xl font-bold">{data.counts[c.key]}</div>
              <div className="text-[11px] text-muted-foreground leading-tight mt-1">{c.label}</div>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <p className="text-sm text-slate-400">
            {rows.length} pilot{rows.length === 1 ? "" : "s"} · sorted by longest inactive first
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={copyEmails} disabled={!rows.length} data-testid="copy-emails-btn">
              <Copy className="h-4 w-4 mr-1.5" /> Copy emails
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={downloadCsv} disabled={!rows.length} data-testid="download-csv-btn">
              <Download className="h-4 w-4 mr-1.5" /> CSV
            </Button>
          </div>
        </div>

        <div className="rounded-sm border border-border/70 bg-card overflow-hidden" data-testid="reactivation-table">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-border/60 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <div className="col-span-3">Pilot</div>
            <div className="col-span-4">Email</div>
            <div className="col-span-3">Status</div>
            <div className="col-span-2 text-right">Last active</div>
          </div>
          {rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No pilots in this cohort. 🎉</div>
          ) : rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border/40 text-sm items-center" data-testid={`reactivation-row-${i}`}>
              <div className="col-span-3 font-medium truncate">{r.name || "—"}</div>
              <div className="col-span-4 text-slate-300 truncate font-mono text-xs">{r.email}</div>
              <div className="col-span-3 text-slate-400 text-xs">{r.status}</div>
              <div className="col-span-2 text-right text-xs flex items-center justify-end gap-1">
                <Clock className="h-3 w-3 text-muted-foreground" />
                {r.days_since_active == null ? "—" : r.days_since_active === 0 ? "today" : `${r.days_since_active}d ago`}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          Tip: your highest-ROI move is a short personal email to the "Started, didn't finish Lesson 1" cohort — copy their addresses above.
        </p>
      </main>
    </div>
  );
}
